import React from 'react';
import { Globe, Edit3, Save } from 'lucide-react';

export interface SeoConfig {
  testMode: boolean;
  metaNoIndex: boolean; // Renamed from noIndexEnabled for clarity
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string;
  ogSiteName: string;
  ogImage: string;
  twitterSite: string;
  sitemapRoutes: string; // Comma-separated list of routes for sitemap
}

interface SeoConfigSectionProps {
  config: SeoConfig;
  onConfigChange: (config: SeoConfig) => void;
  onSave: () => void;
}

/**
 * Composant pour la configuration SEO et l'indexation
 */
const SeoConfigSection: React.FC<SeoConfigSectionProps> = ({
  config,
  onConfigChange,
  onSave
}) => {
  const handleChange = (field: keyof SeoConfig, value: string | boolean) => {
    onConfigChange({ ...config, [field]: value });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center mb-6">
        <Globe className="mr-3 text-blue-600" size={28} />
        <h2 className="text-2xl font-semibold text-gray-800">Configuration SEO Globale</h2>
      </div>

      {/* Section Indexation & Robots */}
      <div className="mb-8 p-4 border border-gray-200 rounded-md">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Indexation et Robots</h3>
        <div className="space-y-4">
          <div className="flex flex-col">
            <div className="flex items-center mb-1">
              <input
                type="checkbox"
                id="testMode"
                checked={config.testMode}
                onChange={(e) => handleChange('testMode', e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <label htmlFor="testMode" className="text-gray-700 font-medium">
                Mode Test Global (noindex sur tout le site)
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6">
              Active la balise "noindex, nofollow" sur toutes les pages. Utile en développement.
            </p>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center mb-1">
              <input
                type="checkbox"
                id="metaNoIndex"
                checked={config.metaNoIndex}
                onChange={(e) => handleChange('metaNoIndex', e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <label htmlFor="metaNoIndex" className="text-gray-700 font-medium">
                Désactiver l'indexation par défaut (meta robots)
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6">
              Contrôle la balise meta robots par défaut pour les nouvelles pages ou celles non configurées.
            </p>
          </div>
        </div>
      </div>

      {/* Section Meta Tags par Défaut */}
      <div className="mb-8 p-4 border border-gray-200 rounded-md">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Meta Tags par Défaut</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="defaultTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Titre par défaut du site
            </label>
            <input
              type="text"
              id="defaultTitle"
              value={config.defaultTitle}
              onChange={(e) => handleChange('defaultTitle', e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="defaultDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description par défaut du site
            </label>
            <textarea
              id="defaultDescription"
              value={config.defaultDescription}
              onChange={(e) => handleChange('defaultDescription', e.target.value)}
              rows={3}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="defaultKeywords" className="block text-sm font-medium text-gray-700 mb-1">
              Mots-clés par défaut (séparés par des virgules)
            </label>
            <input
              type="text"
              id="defaultKeywords"
              value={config.defaultKeywords}
              onChange={(e) => handleChange('defaultKeywords', e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Section Open Graph & Twitter */}
      <div className="mb-8 p-4 border border-gray-200 rounded-md">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Open Graph & Twitter Cards</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="ogSiteName" className="block text-sm font-medium text-gray-700 mb-1">
              Nom du site Open Graph (og:site_name)
            </label>
            <input
              type="text"
              id="ogSiteName"
              value={config.ogSiteName}
              onChange={(e) => handleChange('ogSiteName', e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="ogImage" className="block text-sm font-medium text-gray-700 mb-1">
              URL de l'image par défaut Open Graph (og:image)
            </label>
            <input
              type="text"
              id="ogImage"
              value={config.ogImage}
              onChange={(e) => handleChange('ogImage', e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.com/default-og-image.jpg"
            />
          </div>
          <div>
            <label htmlFor="twitterSite" className="block text-sm font-medium text-gray-700 mb-1">
              Compte Twitter du site (ex: @VotreCompte)
            </label>
            <input
              type="text"
              id="twitterSite"
              value={config.twitterSite}
              onChange={(e) => handleChange('twitterSite', e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="@VotreCompteTwitter"
            />
          </div>
        </div>
      </div>

      {/* Section Sitemap */}
      <div className="mb-8 p-4 border border-gray-200 rounded-md">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Configuration du Sitemap</h3>
        <div>
          <label htmlFor="sitemapRoutes" className="block text-sm font-medium text-gray-700 mb-1">
            Routes à inclure dans le sitemap (séparées par des virgules)
          </label>
          <textarea
            id="sitemapRoutes"
            value={config.sitemapRoutes}
            onChange={(e) => handleChange('sitemapRoutes', e.target.value)}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder="/, /contact, /features, /pricing"
          />
          <p className="text-xs text-gray-500 mt-1">
            Ces routes seront utilisées pour générer le fichier sitemap.xml lors du build.
          </p>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={onSave}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
        >
          <Save size={18} className="mr-2" />
          Enregistrer les Paramètres SEO
        </button>
      </div>
    </div>
  );
};

export default SeoConfigSection;
